import React from 'react';
import { Activity, Scale, Ruler, Heart, FlameIcon, Percent, Dumbbell, Info } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета процента жира
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Получение категории процента жира
  const getCategoryInfo = (category) => {
    const categories = {
      athletic: {
        title: 'Атлетический',
        description: 'Низкий уровень жира, характерный для профессиональных атлетов',
        color: 'green'
      },
      fitness: {
        title: 'Фитнес',
        description: 'Здоровый низкий уровень жира, характерный для физически активных людей',
        color: 'emerald'
      },
      average: {
        title: 'Средний',
        description: 'Средний уровень жира, приемлемый для большинства людей',
        color: 'blue'
      },
      overweight: {
        title: 'Повышенный',
        description: 'Уровень жира выше среднего, может представлять риск для здоровья',
        color: 'orange'
      },
      obese: {
        title: 'Ожирение',
        description: 'Высокий уровень жира, повышенный риск для здоровья',
        color: 'red'
      }
    };

    return categories[category] || {
      title: 'Не определена',
      description: 'Не удалось определить категорию',
      color: 'gray'
    };
  };

  const categoryInfo = getCategoryInfo(results.category);

  // Определение цвета для диапазона нормы
  const getPercentColor = (value) => {
    if (value <= results.optimalRange.min) return 'text-emerald-500 dark:text-emerald-400';
    if (value <= results.optimalRange.max) return 'text-emerald-600 dark:text-emerald-300';
    if (value <= results.optimalRange.average) return 'text-yellow-500 dark:text-yellow-400';
    if (value <= results.optimalRange.obese) return 'text-orange-500 dark:text-orange-400';
    return 'text-red-500 dark:text-red-400';
  };

  return (
    <ToolResult title="Результаты оценки состава тела">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Процент жира */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Percent className="w-4 h-4"/>
            Процент жира в организме
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold ${getPercentColor(results.bodyFatPercentage)}`} aria-live="polite">
              {formatNumber(results.bodyFatPercentage, 1)}%
            </span>
            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
              ({categoryInfo.title})
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Оптимальный диапазон: {results.optimalRange.min}–{results.optimalRange.max}%
          </p>
        </div>

        {/* Состав тела */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Dumbbell className="w-4 h-4"/>
            Масса тела
          </h3>
          <div className="grid grid-cols-2 gap-1 text-sm">
            <div>
              <span className="block text-blue-600 dark:text-blue-400">Жировая масса:</span>
              <span className="text-xl font-bold text-blue-800 dark:text-blue-200">
                {formatNumber(results.bodyComposition.fatMass, 1)} кг
              </span>
            </div>
            <div>
              <span className="block text-indigo-600 dark:text-indigo-400">Безжировая масса:</span>
              <span className="text-xl font-bold text-indigo-800 dark:text-indigo-200">
                {formatNumber(results.bodyComposition.leanMass, 1)} кг
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Метод расчета" 
          value={results.bodyFatMethod} 
          color="emerald" 
          icon={<Activity className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Вес" 
          value={`${formatNumber(results.weight, 1)} кг`} 
          color="gray" 
          icon={<Scale className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Рост" 
          value={`${results.height} см`} 
          icon={<Ruler className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Индекс массы тела (ИМТ)" 
          value={`${formatNumber(results.bmi, 1)} кг/м²`} 
          color="blue"
          icon={<Percent className="w-4 h-4"/>} 
        />

        <ResultItem 
          label="Базовый метаболизм (BMR)" 
          value={`${formatNumber(results.metabolic.bmr, 0)} ккал/день`} 
          color="orange"
          icon={<FlameIcon className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Общий расход энергии (TDEE)" 
          value={`${formatNumber(results.metabolic.tdee, 0)} ккал/день`} 
          color="orange" 
        />
        
        <ResultItem 
          label="Для снижения веса" 
          value={`${formatNumber(results.metabolic.caloricDeficit, 0)} ккал/день`} 
          color="green" 
        />
        
        <ResultItem 
          label="Для набора массы" 
          value={`${formatNumber(results.metabolic.caloricSurplus, 0)} ккал/день`} 
          color="blue" 
        />

        {/* Информация о категории */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Heart className={`w-5 h-5 text-${categoryInfo.color}-500`} />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {categoryInfo.title} уровень жира
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {categoryInfo.description}. 
                {results.category === 'athletic' && ' Поддерживайте текущий уровень активности и питания.'}
                {results.category === 'fitness' && ' Сбалансированное питание и регулярные тренировки помогут поддерживать этот уровень.'}
                {results.category === 'average' && ' Регулярная физическая активность и контроль питания будут полезны для здоровья.'}
                {results.category === 'overweight' && ' Рекомендуется пересмотреть рацион питания и увеличить физическую активность.'}
                {results.category === 'obese' && ' Рекомендуется обратиться к специалисту для разработки программы снижения веса.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Дополнительная информация о точности метода */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <Info className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              О точности расчетов
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Расчет процента жира является приблизительным. Точность зависит от выбранного метода и корректности измерений. 
              {results.bodyFatMethod.includes('ВМС') && ' Метод окружностей тела имеет погрешность ±3-4%.'}
              {results.bodyFatMethod.includes('Джексона-Поллока') && ' Метод калиперометрии имеет погрешность ±2-3%.'}
              {' '}Для более точной оценки обратитесь к специалисту.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;